import React, { createContext, useEffect, useMemo, useState } from "react";
import { TextTemplateKey } from "../components/types";
import { useTenant } from "../hooks/context-provider-hooks/use-tenant";
import { getLocalizedString, makeScopedTextTemplateKey } from "../utils/string-utils";
import { LocalizedTextContextProviderProps, LocalizedTextState, LocalizedTextStateSetters } from "./types";

export const LocalizedTextStateContext = createContext<LocalizedTextState<TextTemplateKey>>(undefined);
LocalizedTextStateContext.displayName = "LocalizedTextStateContext";

export const LocalizedTextStateSetterContext = createContext<LocalizedTextStateSetters<TextTemplateKey>>(undefined);
LocalizedTextStateSetterContext.displayName = "LocalizedTextStateSetterContext";

/**
 * A context provider that provides helpers for rendering localized text templates, given a mapping of keys to language
 * specific localized text templates.
 *
 * If used within a `TenantContextProvider` it can also render text templates that are not just language dependent but
 * also tenant aware. This can be used to render language + tenant specific text content in an application that supports
 * multitenancy.
 *
 * It has 3 companion hooks to use its functionality:
 *
 * ### `useLanguage`
 *
 * This hook returns the current language state value maintained by this provider.
 *
 * ### `useSetLanguage`
 *
 * This hook returns a setter function to change the active language at run-tim. This updates any component in sub-tree
 * under this provider that uses the `useLocalizedText` to render localized text strings, thus updating app content
 * language.
 *
 * ### `useLocalizedText`
 *
 * Use this hook to get a helper function that translates special keys to localizes text strings.
 * Note that the returned helper function is tenant aware. But for that feature to work correctly this provider must
 * be used within a properly initialized `TenantProvider`.
 */
export const LocalizedTextContextProvider = <T extends TextTemplateKey>({
  language: languageProp,
  textTemplatesByLanguage: textTemplatesByLanguageProp,
  tenant: tenantProp,
  children,
}: LocalizedTextContextProviderProps<T>) => {
  const [language, setLanguage] = useState(languageProp);
  const [textTemplatesByLanguage, setTextTemplatesByLanguage] = useState(textTemplatesByLanguageProp);
  const tenant = useTenant(true);

  useEffect(() => {
    setLanguage((languageState) => (languageProp !== languageState ? languageProp : languageState));
  }, [languageProp]);

  useEffect(() => {
    setTextTemplatesByLanguage((templatesByLanguageState) =>
      textTemplatesByLanguageProp !== templatesByLanguageState ? textTemplatesByLanguageProp : templatesByLanguageState
    );
  }, [textTemplatesByLanguageProp]);

  const localizedText: LocalizedTextState<T>["localizedText"] = (
    textTemplateKey,
    textTemplateData = {},
    withTenant = false,
    defaultText
  ) => {
    const maybeScopes = withTenant ? [tenantProp ?? tenant] : undefined;
    const maybeScopedTextTemplateKey = makeScopedTextTemplateKey(textTemplateKey, maybeScopes);
    return getLocalizedString(
      language,
      textTemplatesByLanguage,
      maybeScopedTextTemplateKey,
      textTemplateData,
      defaultText
    );
  };

  // memoize setters object so that LocalizedTextSetterContext consumers don't re-render unnecessarily
  const stateSetters = useMemo<LocalizedTextStateSetters<T>>(() => ({ setLanguage, setTextTemplatesByLanguage }), []);

  return (
    <LocalizedTextStateContext.Provider value={{ language, textTemplatesByLanguage, localizedText }}>
      <LocalizedTextStateSetterContext.Provider value={stateSetters}>
        {children}
      </LocalizedTextStateSetterContext.Provider>
    </LocalizedTextStateContext.Provider>
  );
};
